import * as jsforce from 'jsforce'
import type {
  PRSheetData,
  PRSheet,
  PRSkill,
  PRSkillGroup,
  Career,
  PRExperience,
  PRPortfolioItem,
  PRHighlightEpisode,
  PRAward,
  PRReference,
  PREducation,
  PRLink,
} from '../../types/salesforce'
import { SOBJECT_NAMES } from '../../constants/salesforce'

// PRSheet データ取得
export async function fetchPRSheetData(conn: jsforce.Connection, careerId: string): Promise<PRSheetData> {
  // PRSheet取得
  const prSheetResult = await conn.query<PRSheet>(`
    SELECT Id, Career__c, ProfileImageContentDocumentId__c, PDFContentDocumentId__c, Tags__c, PRImageContentDocumentId__c, PRVideoURL__c,
           Catchphrase__c, Jobs__c, FinalEducation__c,
           ExtracurricularActivities__c, Internship__c, PRSummary__c
    FROM ${SOBJECT_NAMES.PRSHEET}
    WHERE Career__c = '${careerId}'
    LIMIT 1
  `)

  const prSheet = prSheetResult.records[0] || { Career__c: careerId }

  // Career情報取得
  const careerResult = await conn.query<Career>(`
    SELECT Id, Name, FirstName__c, LastName__c, FirstNameKana__c, LastNameKana__c,
           Gender__c, Email__c, Phone__c, PostalCode__c, AddressState__c, AddressCity__c, Address__c,
           DS_C__c, DS_DTM__c, DS_ccc__c, DS_Swift__c, DS_PHP__c, DS_Java__c, DS_Ruby__c, DS_Python__c,
           DS_SQL__c, HTMLCSS__c, ds_dart__c, DS_Go__c, DS_JavaScript__c, DS_Rust__c, DS_Typescript__c,
           DS_Scala__c, DS_GSLS__c, DS_Kotlin__c, ds_p5_js__c, DS_ObC__c,
           ds_unrealengine__c, DS_Unity__c, DS_C_sharp__c,
           design_skill__c, videoproduction_skill__c
    FROM ${SOBJECT_NAMES.CAREER}
    WHERE Id = '${careerId}'
    LIMIT 1
  `)

  const career = careerResult.records[0]

  if (!career) {
    throw new Error('Career not found')
  }

  // PRSkillGroup取得（SortOrder__cで並び替え）
  let skillGroupsResult
  if (prSheet.Id) {
    skillGroupsResult = await conn.query<PRSkillGroup>(`
      SELECT Id, Name, PRSheet__c, SortOrder__c
      FROM ${SOBJECT_NAMES.PRSKILL_GROUP}
      WHERE PRSheet__c = '${prSheet.Id}'
      ORDER BY SortOrder__c ASC NULLS LAST, CreatedDate ASC
    `)
    console.log(
      'Loaded skill groups:',
      skillGroupsResult.records.map((g) => ({
        Id: g.Id,
        Name: g.Name,
        SortOrder__c: g.SortOrder__c,
      })),
    )
  } else {
    skillGroupsResult = { records: [], totalSize: 0, done: true }
  }

  // PRSkill取得（SortOrder__cで並び替え）
  let skillsResult
  if (prSheet.Id) {
    skillsResult = await conn.query<PRSkill>(`
      SELECT Id, Name, Rating__c, Description__c, SkillGroup__c, PRSheet__c, SortOrder__c
      FROM ${SOBJECT_NAMES.PRSKILL}
      WHERE PRSheet__c = '${prSheet.Id}'
      ORDER BY SortOrder__c ASC NULLS LAST, CreatedDate ASC
    `)
  } else {
    skillsResult = { records: [], totalSize: 0, done: true }
  }

  // 新規オブジェクトのデータ取得
  let experiencesResult,
    portfolioItemsResult,
    highlightEpisodesResult,
    awardsResult,
    referencesResult,
    educationsResult,
    linksResult

  if (prSheet.Id) {
    ;[
      experiencesResult,
      portfolioItemsResult,
      highlightEpisodesResult,
      awardsResult,
      referencesResult,
      educationsResult,
      linksResult,
    ] = await Promise.all([
      conn.query<PRExperience>(`
        SELECT Id, PRSheet__c, Year__c, Title__c, Overview__c, Results__c, SortOrder__c
        FROM ${SOBJECT_NAMES.EXPERIENCE}
        WHERE PRSheet__c = '${prSheet.Id}'
        ORDER BY SortOrder__c ASC NULLS LAST, CreatedDate ASC
      `),
      conn.query<PRPortfolioItem>(`
        SELECT Id, PRSheet__c, Title__c, ImageContentDocumentId__c, Overview__c, Role__c, URL__c, SortOrder__c
        FROM ${SOBJECT_NAMES.PORTFOLIO_ITEM}
        WHERE PRSheet__c = '${prSheet.Id}'
        ORDER BY SortOrder__c ASC NULLS LAST, CreatedDate ASC
      `),
      conn.query<PRHighlightEpisode>(`
        SELECT Id, PRSheet__c, Title__c, ImageContentDocumentId__c, Summary__c, Goal__c, Challenges__c, Team__c, Role__c, Actions__c, Results__c, SortOrder__c
        FROM ${SOBJECT_NAMES.HIGHLIGHT_EPISODE}
        WHERE PRSheet__c = '${prSheet.Id}'
        ORDER BY SortOrder__c ASC NULLS LAST, CreatedDate ASC
      `),
      conn.query<PRAward>(`
        SELECT Id, PRSheet__c, Date__c, AwardName__c, Details__c, SortOrder__c
        FROM ${SOBJECT_NAMES.AWARD}
        WHERE PRSheet__c = '${prSheet.Id}'
        ORDER BY SortOrder__c ASC NULLS LAST, CreatedDate ASC
      `),
      conn.query<PRReference>(`
        SELECT Id, PRSheet__c, Title__c, ReferrerName__c, ReferrerTitle__c, Comment__c, SortOrder__c
        FROM ${SOBJECT_NAMES.REFERENCE}
        WHERE PRSheet__c = '${prSheet.Id}'
        ORDER BY SortOrder__c ASC NULLS LAST, CreatedDate ASC
      `),
      conn.query<PREducation>(`
        SELECT Id, PRSheet__c, Year__c, StartDate__c, EndDate__c, SchoolName__c, Department__c, Status__c, SortOrder__c
        FROM ${SOBJECT_NAMES.EDUCATION}
        WHERE PRSheet__c = '${prSheet.Id}'
        ORDER BY SortOrder__c ASC NULLS LAST, CreatedDate ASC
      `),
      conn.query<PRLink>(`
        SELECT Id, PRSheet__c, LinkName__c, URL__c, SortOrder__c
        FROM ${SOBJECT_NAMES.LINK}
        WHERE PRSheet__c = '${prSheet.Id}'
        ORDER BY SortOrder__c ASC NULLS LAST, CreatedDate ASC
      `),
    ])
  } else {
    // PRSheetが存在しない場合は空配列
    experiencesResult = { records: [], totalSize: 0, done: true }
    portfolioItemsResult = { records: [], totalSize: 0, done: true }
    highlightEpisodesResult = { records: [], totalSize: 0, done: true }
    awardsResult = { records: [], totalSize: 0, done: true }
    referencesResult = { records: [], totalSize: 0, done: true }
    educationsResult = { records: [], totalSize: 0, done: true }
    linksResult = { records: [], totalSize: 0, done: true }
  }

  return {
    prSheet,
    career,
    skills: skillsResult.records,
    skillGroups: skillGroupsResult.records,
    experiences: experiencesResult.records,
    portfolioItems: portfolioItemsResult.records,
    highlightEpisodes: highlightEpisodesResult.records,
    awards: awardsResult.records,
    references: referencesResult.records,
    educations: educationsResult.records,
    links: linksResult.records,
  }
}

// PRSheet 保存
export async function savePRSheet(conn: jsforce.Connection, prSheet: PRSheet): Promise<string> {
  console.log('savePRSheet called with:', prSheet)

  if (prSheet.Id) {
    const updateData = { ...prSheet, Id: prSheet.Id }
    console.log('Updating PRSheet with data:', updateData)
    const result = await conn.sobject(SOBJECT_NAMES.PRSHEET).update(updateData)
    console.log('Update result:', result)
    return prSheet.Id
  } else {
    console.log('Creating new PRSheet with data:', prSheet)
    const result = await conn.sobject(SOBJECT_NAMES.PRSHEET).create(prSheet)
    console.log('Create result:', result)
    return result.id!
  }
}

// PRSkills 保存（グループIDマッピングを受け取る）
export async function savePRSkills(
  conn: jsforce.Connection,
  skills: PRSkill[],
  deletedIds: string[],
  groupIdMapping: Map<string, string>,
): Promise<void> {
  console.log('savePRSkills called:', {
    skillsCount: skills.length,
    deletedIds,
    groupIdMapping: Object.fromEntries(groupIdMapping),
  })

  // 削除（temp IDで始まらない実際のIDのみ削除）
  const realDeletedIds = deletedIds.filter((id) => !id.startsWith('temp-'))
  if (realDeletedIds.length > 0) {
    console.log('Deleting skills:', realDeletedIds)
    await conn.sobject(SOBJECT_NAMES.PRSKILL).delete(realDeletedIds)
  }

  // 更新・作成
  const toUpdate: PRSkill[] = []
  const toCreate: Partial<PRSkill>[] = []

  skills.forEach((skill) => {
    // SkillGroup__cがtempIDの場合、実際のIDに置き換え
    let skillGroupId = skill.SkillGroup__c
    if (skillGroupId) {
      if (groupIdMapping.has(skillGroupId)) {
        const originalId = skillGroupId
        skillGroupId = groupIdMapping.get(skillGroupId)
        console.log('Mapping group ID:', originalId, '->', skillGroupId)
      } else if (skillGroupId.startsWith('temp-')) {
        // tempIDがマッピングに存在しない場合はnullに設定（グループ未設定として扱う）
        console.warn('Group ID not found in mapping, setting to null:', skillGroupId)
        skillGroupId = undefined
      }
    }

    // 一時IDを持つものは新規作成として扱う
    if (skill.Id && !skill.Id.startsWith('temp-')) {
      toUpdate.push({ ...skill, SkillGroup__c: skillGroupId })
    } else {
      // 一時IDを削除して新規作成
      const { Id: _Id, ...skillWithoutId } = skill
      toCreate.push({ ...skillWithoutId, SkillGroup__c: skillGroupId })
    }
  })

  console.log('Skills to update:', toUpdate.length, toUpdate)
  console.log('Skills to create:', toCreate.length, toCreate)

  if (toUpdate.length > 0) {
    // Master-Detail関係のPRSheet__cは更新時に含めない
    const updateResult = await conn.sobject(SOBJECT_NAMES.PRSKILL).update(
      toUpdate.map((s) => {
        const { PRSheet__c: _PRSheet__c, ...updateFields } = s
        return { ...updateFields, Id: s.Id! }
      }),
    )
    console.log('Update result:', updateResult)
  }

  if (toCreate.length > 0) {
    const createResult = await conn.sobject(SOBJECT_NAMES.PRSKILL).create(toCreate)
    console.log('Create result:', createResult)
  }
}

// PRSkillGroups 保存（temp IDとSalesforce IDのマッピングを返す）
export async function savePRSkillGroups(
  conn: jsforce.Connection,
  groups: PRSkillGroup[],
  deletedIds: string[],
): Promise<Map<string, string>> {
  console.log('savePRSkillGroups called:', {
    groupsCount: groups.length,
    groups: groups.map((g) => ({
      Id: g.Id,
      Name: g.Name,
      SortOrder__c: g.SortOrder__c,
    })),
    deletedIds,
  })

  const idMapping = new Map<string, string>() // tempId -> salesforceId

  // 削除（temp IDで始まらない実際のIDのみ削除）
  const realDeletedIds = deletedIds.filter((id) => !id.startsWith('temp-'))
  if (realDeletedIds.length > 0) {
    console.log('Deleting groups:', realDeletedIds)
    await conn.sobject(SOBJECT_NAMES.PRSKILL_GROUP).delete(realDeletedIds)
  }

  // 更新・作成
  const toUpdate: PRSkillGroup[] = []
  const toCreate: Array<{ tempId: string; group: Partial<PRSkillGroup> }> = []

  groups.forEach((group) => {
    // 一時IDを持つものは新規作成として扱う
    if (group.Id && !group.Id.startsWith('temp-')) {
      toUpdate.push(group)
      idMapping.set(group.Id, group.Id) // 既存IDはそのまま
    } else {
      // 一時IDを削除して新規作成
      const tempId = group.Id!
      const { Id: _Id, ...groupWithoutId } = group
      toCreate.push({ tempId, group: groupWithoutId })
    }
  })

  console.log(
    'Groups to update:',
    toUpdate.length,
    toUpdate.map((g) => ({
      Id: g.Id,
      Name: g.Name,
      SortOrder__c: g.SortOrder__c,
    })),
  )
  console.log(
    'Groups to create:',
    toCreate.length,
    toCreate.map((item) => ({
      tempId: item.tempId,
      Name: item.group.Name,
      SortOrder__c: item.group.SortOrder__c,
    })),
  )

  if (toUpdate.length > 0) {
    // Master-Detail関係のPRSheet__cは更新時に含めない
    const updateData = toUpdate.map((g) => {
      const { PRSheet__c: _PRSheet__c, ...updateFields } = g
      return { ...updateFields, Id: g.Id! }
    })
    console.log('Updating groups with data:', updateData)
    const updateResult = await conn.sobject(SOBJECT_NAMES.PRSKILL_GROUP).update(updateData)
    console.log('Update result:', updateResult)
  }

  if (toCreate.length > 0) {
    const createData = toCreate.map((item) => item.group)
    console.log('Creating groups with data:', createData)
    const createResults = await conn.sobject(SOBJECT_NAMES.PRSKILL_GROUP).create(createData)
    console.log('Create result:', createResults)
    const resultsArray = Array.isArray(createResults) ? createResults : [createResults]
    resultsArray.forEach((result, index) => {
      if (result.success && result.id) {
        idMapping.set(toCreate[index].tempId, result.id)
      }
    })
  }

  return idMapping
}

/**
 * 汎用的なsave関数（create/update/delete対応）
 * @param conn Salesforce接続
 * @param sobjectName オブジェクト名
 * @param records 保存するレコード配列
 * @param deletedIds 削除するレコードID配列
 * @returns void
 */
async function saveRecords<T extends { Id?: string; PRSheet__c?: string }>(
  conn: jsforce.Connection,
  sobjectName: string,
  records: T[],
  deletedIds: string[],
): Promise<void> {
  // 削除（temp IDで始まらない実際のIDのみ削除）
  const realDeletedIds = deletedIds.filter((id) => !id.startsWith('temp-'))
  if (realDeletedIds.length > 0) {
    console.log(`Deleting ${sobjectName}:`, realDeletedIds)
    await conn.sobject(sobjectName).delete(realDeletedIds)
  }

  // 更新・作成
  const toUpdate: T[] = []
  const toCreate: Partial<T>[] = []

  records.forEach((record) => {
    // 一時IDを持つものは新規作成として扱う
    if (record.Id && !record.Id.startsWith('temp-')) {
      toUpdate.push(record)
    } else {
      // 一時IDを削除して新規作成
      const { Id: _Id, ...recordWithoutId } = record
      toCreate.push(recordWithoutId as Partial<T>)
    }
  })

  console.log(`${sobjectName} to update:`, toUpdate.length)
  console.log(`${sobjectName} to create:`, toCreate.length)

  if (toUpdate.length > 0) {
    // Master-Detail関係のPRSheet__cは更新時に含めない
    const updateResult = await conn.sobject(sobjectName).update(
      toUpdate.map((r) => {
        const { PRSheet__c: _PRSheet__c, ...updateFields } = r
        return { ...updateFields, Id: r.Id! }
      }),
    )
    console.log(`${sobjectName} update result:`, updateResult)
  }

  if (toCreate.length > 0) {
    const createResult = await conn.sobject(sobjectName).create(toCreate)
    console.log(`${sobjectName} create result:`, createResult)
  }
}

// PRExperience 保存
export async function savePRExperiences(
  conn: jsforce.Connection,
  experiences: PRExperience[],
  deletedIds: string[],
): Promise<void> {
  await saveRecords(conn, SOBJECT_NAMES.EXPERIENCE, experiences, deletedIds)
}

// PRPortfolioItem 保存
export async function savePRPortfolioItems(
  conn: jsforce.Connection,
  portfolioItems: PRPortfolioItem[],
  deletedIds: string[],
): Promise<void> {
  await saveRecords(conn, SOBJECT_NAMES.PORTFOLIO_ITEM, portfolioItems, deletedIds)
}

// PRHighlightEpisode 保存
export async function savePRHighlightEpisodes(
  conn: jsforce.Connection,
  highlightEpisodes: PRHighlightEpisode[],
  deletedIds: string[],
): Promise<void> {
  await saveRecords(conn, SOBJECT_NAMES.HIGHLIGHT_EPISODE, highlightEpisodes, deletedIds)
}

// PRAward 保存
export async function savePRAwards(conn: jsforce.Connection, awards: PRAward[], deletedIds: string[]): Promise<void> {
  await saveRecords(conn, SOBJECT_NAMES.AWARD, awards, deletedIds)
}

// PRReference 保存
export async function savePRReferences(
  conn: jsforce.Connection,
  references: PRReference[],
  deletedIds: string[],
): Promise<void> {
  await saveRecords(conn, SOBJECT_NAMES.REFERENCE, references, deletedIds)
}

// PREducation 保存
export async function savePREducations(
  conn: jsforce.Connection,
  educations: PREducation[],
  deletedIds: string[],
): Promise<void> {
  await saveRecords(conn, SOBJECT_NAMES.EDUCATION, educations, deletedIds)
}

// PRLink 保存
export async function savePRLinks(conn: jsforce.Connection, links: PRLink[], deletedIds: string[]): Promise<void> {
  await saveRecords(conn, SOBJECT_NAMES.LINK, links, deletedIds)
}

// Career__cのスキルフィールドを更新
export async function updateCareerSkills(
  conn: jsforce.Connection,
  careerId: string,
  skillRatings: Record<string, string | null>, // フィールド名 → レーティング値 (S/A/B/C/D/E)
): Promise<void> {
  // アップデート用のデータを作成（Partial<Career>とIdの組み合わせ）
  const updateData: Partial<Career> & { Id: string } = { Id: careerId }

  Object.entries(skillRatings).forEach(([field, value]) => {
    // Career型に存在するフィールドのみ設定
    if (field in updateData || field.endsWith('__c')) {
      ;(updateData as Record<string, string | null>)[field] = value
    }
  })

  console.log('Updating Career skills:', updateData)

  await conn.sobject(SOBJECT_NAMES.CAREER).update(updateData)
}
